import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ProviderToken,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { SyllabusQuery, SyllabusService } from '@examdojo/category';
import { ImageChatRealtimeService, provideImageChat } from '@examdojo/chat';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { PdfViewerComponent } from '@examdojo/core/pdf-viewer';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { ToastService } from '@examdojo/core/toast';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import {
  JsonEditorTriggerDirective,
  NewQuestionItemButtonComponent,
  provideQuestionItemsForm,
  QuestionActiveItemEditorComponent,
  QuestionItemsEditorComponent,
} from '@examdojo/question';
import {
  ReferenceQuestionQuery,
  ReferenceQuestionService,
  ReferenceQuestionStatusSelectorComponent,
} from '@examdojo/reference-question';
import { ReferenceQuestionHistoryDialogService } from '@examdojo/review';
import { RawSourceItem, SourceQuery, SourceService } from '@examdojo/source';
import { DbJson } from '@examdojo/supabase';
import { LoaderComponent } from '@examdojo/ui/loader';
import { AngularSplitModule } from 'angular-split';
import isEqual from 'lodash/isEqual';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  merge,
  of,
  skip,
  switchMap,
  tap,
} from 'rxjs';
import { ScopedQuestionListComponent } from '../../question/scoped-list/scoped-question-list.component';
import { CmsUrlParts } from '../cms.model';
import { DetailViewWrapperComponent } from '../detail-view/wrapper/detail-view-wrapper.component';
import { ReferenceQuestionImageChatEffectService } from './effects/reference-question-image-chat-effect.service';
import { ReferenceQuestionEditorItemsFormService } from './question-items-form/reference-question-editor-items-form-service';
import { ReferenceQuestionEditorService } from './question-items-form/reference-question-editor.service';

@Component({
  selector: 'exam-cms-reference-questions-container',
  templateUrl: './cms-reference-questions-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [
    QuestionItemsEditorComponent,
    QuestionActiveItemEditorComponent,
    DetailViewWrapperComponent,
    ButtonComponent,
    AngularSplitModule,
    NewQuestionItemButtonComponent,
    JsonEditorTriggerDirective,
    IconComponent,
    ReferenceQuestionStatusSelectorComponent,
    RouterLink,
    IconButtonComponent,
    MatTooltipModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    PdfViewerComponent,
    LoaderComponent,
    ScopedQuestionListComponent,
  ],
  providers: [
    ReferenceQuestionEditorService,
    provideQuestionItemsForm(ReferenceQuestionEditorItemsFormService),
    provideImageChat(),
    ReferenceQuestionImageChatEffectService,
  ],
  animations: [getAnimationAppearDisappear()],
})
export class CmsReferenceQuestionsContainerComponent {
  constructor(
    private readonly referenceQuestionQuery: ReferenceQuestionQuery,
    private readonly referenceQuestionEditorItemsFormService: ReferenceQuestionEditorItemsFormService,
    private readonly referenceQuestionEditorService: ReferenceQuestionEditorService,
    private readonly destroyRef: DestroyRef,
    private readonly referenceQuestionService: ReferenceQuestionService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly sourceQuery: SourceQuery,
    private readonly sourceService: SourceService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly syllabusService: SyllabusService,
    private readonly router: Router,
    private readonly referenceQuestionHistoryDialogService: ReferenceQuestionHistoryDialogService,
  ) {
    merge(this.saveReferenceQuestionStatusOnControlChange(), this.updateActiveSyllabusOnReferenceQuestionChange())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [ReferenceQuestionImageChatEffectService, ImageChatRealtimeService].map(
    (service: ProviderToken<unknown>) => inject(service),
  );

  readonly CmsUrlParts = CmsUrlParts;
  private readonly initialized$ = this.referenceQuestionEditorService.activeReferenceQuestionFetched$;

  readonly questionItemsForm = this.referenceQuestionEditorItemsFormService.form;

  readonly isSaving = signal<boolean>(false);
  readonly showPdfViewer = signal(false);
  readonly pdfViewer = viewChild<PdfViewerComponent>('pdfViewer');
  readonly pdfViewer$ = toObservable(this.pdfViewer).pipe(filter(isNotNullish));
  readonly sidebarTab = signal<'details' | 'questions' | 'history'>('details');
  readonly isCreatingSeedQuestion = signal<boolean>(false);

  readonly selectedPdfPlace = signal<RawSourceItem['type']>('questions');

  private readonly source$ = this.sourceQuery.active$;

  readonly pdfUrl$ = this.source$.pipe(
    distinctUntilChanged((a, b) => isEqual(a?.raw_source, b?.raw_source)),
    switchMap((source) =>
      source
        ? forkJoin({
            markschemes: this.sourceService.getRawSourceUrls('markschemes', source),
            questions: this.sourceService.getRawSourceUrls('questions', source),
          })
        : of(null),
    ),
  );

  readonly pdfNumberMetadata$ = combineLatest([
    this.referenceQuestionQuery.active$,
    toObservable(this.selectedPdfPlace),
  ]).pipe(
    map(([refQuestion, selectedPdfPlace]) => {
      if (selectedPdfPlace === 'questions') {
        return refQuestion?.metadata?.pdf_question_page_number;
      }
      return refQuestion?.metadata?.pdf_markscheme_page_number;
    }),
    shareOneReplay(),
    distinctUntilChanged(),
  );

  readonly state = connectState({
    referenceQuestion: this.referenceQuestionQuery.active$,
    initialized: this.initialized$,
    isLoading: this.referenceQuestionQuery.selectLoading(),
    canUndo: this.referenceQuestionEditorItemsFormService.canUndo$,
    canRedo: this.referenceQuestionEditorItemsFormService.canRedo$,
    source: this.source$,
    pdfUrls: this.pdfUrl$,
    pdfNumber: this.pdfNumberMetadata$,
    markCalculationMethod: this.syllabusQuery.markCalculationMethod$,
    syllabus: this.syllabusQuery.active$,
    calculatorAllowed: this.referenceQuestionQuery.active$.pipe(
      map((refQuestion) => refQuestion?.metadata?.calculator_allowed ?? false),
    ),
  });

  readonly activeQuestionItem = this.referenceQuestionEditorItemsFormService.activeQuestionItem;

  readonly statusCtrl = this.referenceQuestionEditorService.referenceQuestionForm.controls.status;

  undo() {
    this.referenceQuestionEditorItemsFormService.undo();
  }
  redo() {
    this.referenceQuestionEditorItemsFormService.redo();
  }

  changeSelectedPdfPlace(value: RawSourceItem['type']) {
    this.selectedPdfPlace.set(value);
  }

  saveQuestionItems() {
    this.isSaving.set(true);

    this.referenceQuestionEditorItemsFormService
      .getProcessedFormData()
      .pipe(
        switchMap((processedData) => {
          assertNonNullable(this.state.referenceQuestion?.id, 'refQuestionId');

          return this.referenceQuestionService.update(this.state.referenceQuestion?.id, {
            question_items: processedData as unknown as DbJson,
          });
        }),
        tap(() => {
          this.questionItemsForm.markAsPristine();
          this.referenceQuestionEditorItemsFormService.setActiveQuestionItem(null);

          this.toastService.success('Question Items updated');
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),

        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  saveReferenceQuestionStatusOnControlChange() {
    return this.statusCtrl.valueChanges.pipe(
      switchMap((status) => {
        assertNonNullable(this.state.referenceQuestion?.id, 'refQuestionId');

        return this.referenceQuestionService
          .update(this.state.referenceQuestion?.id, {
            status,
          })
          .pipe(
            tap(() => {
              this.toastService.success('Status updated');
            }),
            this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
          );
      }),
    );
  }

  scrollToReferenceQuestion() {
    this.pdfNumberMetadata$
      .pipe(
        first(),
        filter(isNotNullish),
        switchMap((pageNumber) => {
          return this.pdfViewer$.pipe(
            first(),
            tap((pdfViewer) => pdfViewer.setPage(pageNumber)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async openHistoryDialog() {
    const referenceQuestionId = this.state.referenceQuestion?.id;
    if (!referenceQuestionId) {
      return;
    }

    await this.referenceQuestionHistoryDialogService.openDialog({
      input: { referenceQuestionId },
    });
  }

  onCalculatorAllowedChange(allowed: boolean): void {
    const refQuestionId = this.state.referenceQuestion?.id;
    if (!refQuestionId) {
      return;
    }

    const currentMetadata = this.state.referenceQuestion?.metadata;
    this.referenceQuestionService
      .update(refQuestionId, {
        metadata: {
          ...(currentMetadata ?? {}),
          calculator_allowed: allowed,
        } as DbJson,
      })
      .pipe(
        tap(() => {
          this.toastService.success('Calculator setting updated');
        }),
      )
      .subscribe();
  }

  createSeedQuestion() {
    assertNonNullable(this.state.referenceQuestion?.id, 'referenceQuestionId');

    this.isCreatingSeedQuestion.set(true);

    this.referenceQuestionService
      .createSeedQuestion(this.state.referenceQuestion.id)
      .pipe(
        switchMap((seedQuestionId) => {
          if (this.state.syllabus?.slug && seedQuestionId) {
            return this.router.navigate([
              '/',
              CmsUrlParts.Syllabus,
              this.state.syllabus.slug,
              CmsUrlParts.Questions,
              seedQuestionId,
            ]);
          }

          return EMPTY;
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isCreatingSeedQuestion.set(false)),
      )
      .subscribe();
  }

  private updateActiveSyllabusOnReferenceQuestionChange() {
    return this.referenceQuestionQuery.active$.pipe(
      filter(isNotNullish),
      switchMap((referenceQuestion) => {
        return this.referenceQuestionService.fetchSyllabusIdByReferenceQuestionId(referenceQuestion.id).pipe(
          tap((syllabusId) => {
            this.syllabusService.setActive(syllabusId ?? null);
          }),
          this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        );
      }),
      switchMap(() => {
        // then react to syllabus changes and redirect to the question list when syllabus changes
        return this.syllabusQuery.selectActive().pipe(
          skip(1),
          tap((syllabus) => {
            if (syllabus) {
              this.router.navigate([CmsUrlParts.Syllabus, syllabus.slug, CmsUrlParts.ReferenceQuestions]);
            }
          }),
        );
      }),
    );
  }
}
